<div class="login-page min-h-screen relative flex items-center justify-center px-4 py-10">

  <div class="login-content relative z-10 w-full max-w-md">

    <!-- Header -->
    <div class="text-center mb-8">
      <div
        class="login-brand-icon relative w-16 h-16 mx-auto mb-5 rounded-2xl
               flex items-center justify-center"
      >
        <div class="absolute inset-[1px] rounded-2xl border border-white/10 pointer-events-none"></div>
        <img src="./icon.png" alt="Chipo Logo" class="w-8 h-8 object-contain relative z-10" />
      </div>

      <p class="text-xs font-bold tracking-[0.22em] uppercase text-[var(--color-muted-foreground)] mb-2">
        Chipo Portal
      </p>
      <h1 class="text-2xl font-bold text-[var(--color-foreground)] tracking-tight">
        Set a new password
      </h1>
      <p class="text-sm text-[var(--color-muted-foreground)] mt-1">
        Choose a strong password for your account
      </p>
    </div>

    <div class="login-card p-8">

      <!-- No token -->
      <ng-container *ngIf="!hasToken">
        <div class="text-center py-2">
          <div class="reset-invalid-icon">
            <i class="bi bi-link-45deg text-2xl"></i>
          </div>
          <h2 class="text-base font-bold text-[var(--color-foreground)] mb-2">
            Invalid or missing link
          </h2>
          <p class="text-sm text-[var(--color-muted-foreground)] leading-relaxed mb-6">
            This reset link is incomplete or has expired. Request a new one from the sign-in page.
          </p>
          <div class="flex flex-col gap-3">
            <button type="button" class="login-btn-primary" (click)="goToLogin()">
              Back to sign in
            </button>
            <button type="button" class="login-btn-secondary w-full" (click)="goToForgotPassword()">
              Request new reset link
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Success -->
      <ng-container *ngIf="hasToken && resetSuccess">
        <div class="text-center py-2">
          <div class="reset-success-icon">
            <svg class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h2 class="text-base font-bold text-[var(--color-foreground)] mb-2">
            Password updated
          </h2>
          <p class="text-sm text-[var(--color-muted-foreground)] leading-relaxed mb-6">
            Your password has been reset successfully. You can now sign in with your new password.
          </p>
          <button type="button" class="login-btn-primary" (click)="goToLogin()">
            Sign in
          </button>
        </div>
      </ng-container>

      <!-- Reset form -->
      <ng-container *ngIf="hasToken && !resetSuccess">
        <div class="reset-token-badge">
          <i class="bi bi-shield-check"></i>
          <span>Secure reset link verified</span>
        </div>

        <div *ngIf="errorMessage" class="login-error-banner mb-5">
          <i class="bi bi-exclamation-circle-fill shrink-0 mt-0.5"></i>
          <span>{{ errorMessage }}</span>
        </div>

        <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>

          <div class="mb-5">
            <label class="login-label block mb-2" for="reset-new-password">
              New password
            </label>
            <div class="login-input-wrap">
              <span class="login-input-icon">
                <i class="bi bi-lock text-sm"></i>
              </span>
              <input
                id="reset-new-password"
                formControlName="newPassword"
                [type]="showNewPassword ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="At least 6 characters"
                class="login-input login-input--with-toggle"
                [class.login-input--invalid]="newPassword?.invalid && newPassword?.touched"
              />
              <button
                type="button"
                class="login-toggle-pw"
                (click)="showNewPassword = !showNewPassword"
                [attr.aria-label]="showNewPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showNewPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
            <p
              *ngIf="newPassword?.hasError('required') && newPassword?.touched"
              class="mt-2 text-xs text-[var(--color-destructive)]"
            >
              New password is required.
            </p>
            <p
              *ngIf="newPassword?.hasError('minlength') && newPassword?.touched"
              class="mt-2 text-xs text-[var(--color-destructive)]"
            >
              Password must be at least 6 characters.
            </p>
          </div>

          <div class="mb-6">
            <label class="login-label block mb-2" for="reset-confirm-password">
              Confirm password
            </label>
            <div class="login-input-wrap">
              <span class="login-input-icon">
                <i class="bi bi-lock-fill text-sm"></i>
              </span>
              <input
                id="reset-confirm-password"
                formControlName="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Re-enter your new password"
                class="login-input login-input--with-toggle"
                [class.login-input--invalid]="(confirmPassword?.invalid && confirmPassword?.touched) || passwordsMismatch"
              />
              <button
                type="button"
                class="login-toggle-pw"
                (click)="showConfirmPassword = !showConfirmPassword"
                [attr.aria-label]="showConfirmPassword ? 'Hide password' : 'Show password'"
              >
                <i [class]="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
            <p
              *ngIf="confirmPassword?.hasError('required') && confirmPassword?.touched"
              class="mt-2 text-xs text-[var(--color-destructive)]"
            >
              Please confirm your password.
            </p>
            <p *ngIf="passwordsMismatch" class="mt-2 text-xs text-[var(--color-destructive)]">
              Passwords do not match.
            </p>
          </div>

          <button type="submit" class="login-btn-primary mb-4" [disabled]="submitting">
            <span
              *ngIf="submitting"
              class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
            ></span>
            <span>{{ submitting ? 'Updating…' : 'Reset password' }}</span>
          </button>

          <button type="button" class="login-btn-secondary w-full" (click)="goToLogin()">
            Back to sign in
          </button>

        </form>
      </ng-container>

    </div>

    <p class="mt-6 text-center text-xs text-[var(--color-muted-foreground)]">
      Links expire after 30 minutes for your security.
    </p>

  </div>

</div>
